// Localização: frontend/src/pages/Financeiro/DashboardFinanceiro.jsx
// Dashboard Financeiro Completo com Drag and Drop

import React, { useEffect, useState, useRef } from 'react';
import Title from '../../components/Layout/Title';
import Card from '../../components/UI/Card';
import CardContent from '../../components/UI/CardContent';
import LoadingIndicator from '../../components/Common/LoadingIndicator';
import Alert from '../../components/UI/Alert';
import { useDashboardFinanceiro } from '../../hooks/useFinanceiro';
import { useDashboardLayout } from '../../hooks/useDashboardLayout';
import CardResumoFinanceiro from '../../components/Financeiro/CardResumoFinanceiro';
import CardSaldoContas from '../../components/Financeiro/CardSaldoContas';
import AcessoRapidoFinanceiro from '../../components/Financeiro/AcessoRapidoFinanceiro';
import FloatingCalendarFilter from '../../components/Financeiro/FloatingCalendarFilter';
import GraficoComparativo from '../../components/Financeiro/GraficoComparativo';
import GraficoTendencias from '../../components/Financeiro/GraficoTendencias';
import GraficoFluxoCaixaFuturo from '../../components/Financeiro/GraficoFluxoCaixaFuturo';
import IndicadoresSaudeFinanceira from '../../components/Financeiro/IndicadoresSaudeFinanceira';
import GraficoVencimentos from '../../components/Financeiro/GraficoVencimentos';
import GraficoContasPagar from '../../components/Financeiro/GraficoContasPagar';
import GraficoContasReceber from '../../components/Financeiro/GraficoContasReceber';
import DashboardGridLayout from '../../components/Financeiro/DashboardGridLayout';
import { 
    TrendingUp, 
    TrendingDown, 
    Wallet, 
    AlertTriangle,
    DollarSign,
    ChevronDown,
    ChevronUp,
    Clock,
    ClockArrowUp,
    ClockArrowDown,
    CalendarDays,
    Edit3,
    Calendar
} from 'lucide-react';

const DashboardFinanceiro = () => {
    const {
        resumo,
        fluxoCaixa,
        previsao,
        contasPagar,
        contasReceber,
        loading,
        error,
        fetchResumo,
        fetchFluxoCaixa,
        fetchPrevisao,
        fetchContasPagar,
        fetchContasReceber
    } = useDashboardFinanceiro();

    // Hook para gerenciar o layout do dashboard
    const {
        layout,
        layoutMobile,
        isEditMode,
        isLoading: isLoadingLayout,
        handleLayoutChange,
        toggleEditMode,
        resetLayout,
    } = useDashboardLayout();

    const [periodoFluxo, setPeriodoFluxo] = useState('dia');
    const [dataInicio, setDataInicio] = useState('');
    const [dataFim, setDataFim] = useState('');
    const [periodoPrevisao, setPeriodoPrevisao] = useState(90); // Padrão: 90 dias
    const [showPeriodFilter, setShowPeriodFilter] = useState(false);
    const periodFilterRef = useRef(null);
    const periodButtonRef = useRef(null);

    // Carregar dados ao montar o componente
    useEffect(() => {
        fetchResumo();
        fetchFluxoCaixa({ periodo: periodoFluxo });
        fetchPrevisao({ dias: periodoPrevisao });
        fetchContasPagar({ periodo: periodoFluxo });
        fetchContasReceber({ periodo: periodoFluxo });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Recarregar previsão quando período mudar
    useEffect(() => {
        if (periodoPrevisao) {
            fetchPrevisao({ dias: periodoPrevisao });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [periodoPrevisao]);

    // Recarregar fluxo de caixa quando período mudar
    useEffect(() => {
        if (periodoFluxo) {
            fetchFluxoCaixa({ 
                periodo: periodoFluxo,
                data_inicio: dataInicio || undefined,
                data_fim: dataFim || undefined
            });
            fetchContasPagar({ 
                periodo: periodoFluxo,
                data_inicio: dataInicio || undefined,
                data_fim: dataFim || undefined
            });
            fetchContasReceber({ 
                periodo: periodoFluxo,
                data_inicio: dataInicio || undefined,
                data_fim: dataFim || undefined
            });
        }
    }, [periodoFluxo, dataInicio, dataFim]);

    // Fechar dropdown ao clicar fora
    useEffect(() => {
        if (!showPeriodFilter) return;

        const handleClickOutside = (event) => {
            if (
                periodFilterRef.current &&
                periodButtonRef.current &&
                !periodFilterRef.current.contains(event.target) &&
                !periodButtonRef.current.contains(event.target)
            ) {
                setShowPeriodFilter(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [showPeriodFilter]);

    // Fechar dropdown ao pressionar Escape
    useEffect(() => {
        if (!showPeriodFilter) return;

        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                setShowPeriodFilter(false);
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [showPeriodFilter]);

    if (loading && !resumo) {
        return (
            <div className="space-y-6">
                <Title title="Dashboard Financeiro" />
                <LoadingIndicator message="Carregando dados do dashboard..." />
            </div>
        );
    }

    if (error && !resumo) {
        return (
            <div className="space-y-6">
                <Title title="Dashboard Financeiro" />
                <Alert type="error" message={error} />
            </div>
        );
    }

    // Preparar widgets para o grid layout
    const widgets = {
        'card-saldo': (
            <CardSaldoContas 
                saldoTotal={resumo?.saldo_total_contas || 0}
                contas={resumo?.contas_bancarias || []}
            />
        ),
        'card-receita-total': (
            <CardResumoFinanceiro
                label="Receita Total"
                value={resumo?.receita_total || 0}
                icon={TrendingUp}
                color="text-green-600"
                bgColor="bg-green-100"
            />
        ),
        'card-despesa-total': (
            <CardResumoFinanceiro
                label="Despesa Total"
                value={resumo?.despesa_total || 0}
                icon={TrendingDown}
                color="text-red-600"
                bgColor="bg-red-100"
            />
        ),
        'card-receitas-pendentes': (
            <CardResumoFinanceiro
                label="Receitas Pendentes"
                value={resumo?.receitas_pendentes || 0}
                icon={ClockArrowUp}
                color="text-green-600"
                bgColor="bg-green-100"
            />
        ),
        'card-vencidas': (
            <CardResumoFinanceiro
                label="Vencidas"
                value={resumo?.contas_vencidas || 0}
                icon={AlertTriangle}
                color="text-orange-600"
                bgColor="bg-orange-100"
                format="number"
            />
        ),
        'card-despesas-pendentes': (
            <CardResumoFinanceiro
                label="Despesas Pendentes"
                value={resumo?.despesas_pendentes || 0}
                icon={ClockArrowDown}
                color="text-red-600"
                bgColor="bg-red-100"
            />
        ),
        'grafico-tendencias': (
            <GraficoTendencias 
                data={fluxoCaixa || []} 
                saldoInicial={resumo?.saldo_total_contas || 0}
                isLoading={loading}
                error={error}
                periodo={periodoFluxo}
            />
        ),
        'card-vencer-7': (
            <CardResumoFinanceiro
                label="A Vencer (7 dias)"
                value={resumo?.contas_a_vencer_7_dias || 0}
                icon={CalendarDays}
                color="text-yellow-600"
                bgColor="bg-yellow-100"
                format="number"
            />
        ),
        'card-vencer-30': (
            <CardResumoFinanceiro
                label="A Vencer (30 dias)"
                value={resumo?.contas_a_vencer_30_dias || 0}
                icon={CalendarDays}
                color="text-blue-600"
                bgColor="bg-blue-100"
                format="number"
            />
        ),
        'grafico-fluxo-futuro': (
            <GraficoFluxoCaixaFuturo 
                previsao={previsao}
                saldoAtualContas={resumo?.saldo_total_contas || 0}
                isLoading={loading && !previsao}
                error={error}
                onPeriodChange={setPeriodoPrevisao}
                periodo={periodoPrevisao}
            />
        ),
        'indicadores-saude': (
            <IndicadoresSaudeFinanceira 
                resumo={resumo}
                isLoading={loading && !resumo}
            />
        ),
        'grafico-vencimentos': (
            <GraficoVencimentos 
                resumo={resumo}
                isLoading={loading && !resumo}
                error={error}
            />
        ),
        'grafico-comparativo': (
            <GraficoComparativo 
                data={fluxoCaixa || []} 
                isLoading={loading}
                error={error}
            />
        ),
        'grafico-contas-pagar': (
            <GraficoContasPagar 
                data={contasPagar || []} 
                isLoading={loading}
                error={error}
                periodo={periodoFluxo}
            />
        ),
        'grafico-contas-receber': (
            <GraficoContasReceber 
                data={contasReceber || []} 
                isLoading={loading}
                error={error}
                periodo={periodoFluxo}
            />
        ),
    };

    return (
        <div className="min-h-screen w-full">
            {/* Acesso Rápido às Funções - Posição Fixa - Centralizado - Abaixo do Header */}
            <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-20 lg:top-20">
                <AcessoRapidoFinanceiro />
            </div>

            {/* Botões Flutuantes à Direita - Período e Edição do Layout */}
            <div className="fixed top-20 right-4 sm:right-6 lg:right-8 z-30 flex flex-col gap-2">
                {/* Botão de Período */}
                <div ref={periodButtonRef}>
                    <button
                        onClick={() => setShowPeriodFilter(!showPeriodFilter)}
                    className={`
                        flex items-center justify-center gap-2
                        px-3 py-2.5 sm:px-4 sm:py-3 rounded-lg
                        transition-all duration-200 font-medium 
                        text-xs sm:text-sm
                        border shadow-lg hover:shadow-xl
                        backdrop-blur-sm
                        ${showPeriodFilter 
                            ? 'bg-gray-100 text-gray-700 border-gray-300 border-[0.5px] shadow-xl font-semibold' 
                            : 'bg-white/80 text-gray-600 border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }
                    `}
                    title={showPeriodFilter ? 'Ocultar filtro de período' : 'Mostrar filtro de período'}
                >
                    <Calendar className={`h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0 ${showPeriodFilter ? 'scale-110' : ''} transition-transform`} />
                    <span className="hidden sm:inline whitespace-nowrap">Período</span>
                </button>
                </div>

                {/* Botão de Personalizar Layout */}
                <button
                    onClick={toggleEditMode}
                    className={`
                        flex items-center justify-center gap-2
                        px-3 py-2.5 sm:px-4 sm:py-3 rounded-lg
                        transition-all duration-200 font-medium 
                        text-xs sm:text-sm
                        border shadow-lg hover:shadow-xl
                        backdrop-blur-sm
                        ${isEditMode 
                            ? 'bg-yellow-100 text-yellow-700 border-yellow-300 border-[0.5px] shadow-xl font-semibold' 
                            : 'bg-white/80 text-yellow-600 border-gray-200 hover:border-yellow-300 hover:bg-yellow-50'
                        }
                    `}
                    title={isEditMode ? 'Salvar layout' : 'Personalizar layout'}
                >
                    <Edit3 className={`h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0 ${isEditMode ? 'scale-110' : ''} transition-transform`} />
                    <span className="hidden sm:inline whitespace-nowrap">Layout</span>
                </button>
            </div>

            {/* Filtro de Calendário Flutuante - Dropdown */}
            {showPeriodFilter && (
                <div ref={periodFilterRef} className="fixed top-32 sm:top-36 right-4 sm:right-6 lg:right-8 z-40 w-[calc(100%-2rem)] sm:w-auto sm:max-w-md">
                    <FloatingCalendarFilter
                        periodo={periodoFluxo}
                        onPeriodoChange={setPeriodoFluxo}
                        startDate={dataInicio}
                        endDate={dataFim}
                        onDateRangeChange={(start, end) => {
                            setDataInicio(start);
                            setDataFim(end);
                        }}
                        noNegativeMargin={true}
                        customZIndex="z-40"
                        showOnlyContent={true}
                    />
                </div>
            )}

            {/* Grid Layout com Drag and Drop - Ocupa toda a página */}
            <div className="pt-32 pb-8 px-4 sm:px-6 lg:px-10">
                {!isLoadingLayout && (
                    <DashboardGridLayout
                        layout={layout}
                        layoutMobile={layoutMobile}
                        onLayoutChange={handleLayoutChange}
                        isEditMode={isEditMode}
                        widgets={widgets}
                    />
                )}
            </div>
        </div>
    );
};

export default DashboardFinanceiro;

